import React, { useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { buildNotification, GenerateNotification } from 'smart-react';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES
} from '../../../constants/eventDataTypes';
import { addTile } from '../Services/SmartChatDashboardService';
import eventBus from '../../../Utils/Common/eventBus';

// `assistantAppKey` should be the SmartApps AppKey (e.g., `sa_tenant_key`) of the
// currently selected assistant. When provided, tiles will be created under that
// assistant's SmartApps context instead of the env-level SMART_APP_APPKEY.
const AddToDashboardModal = ({ message, onClose, onSaved, assistantAppKey }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  // Advanced options are hidden; derive values from message.toolInfo without allowing edits
  const functionName = useMemo(
    () => message?.toolInfo?.function_name || '',
    [message]
  );
  const argumentsJson = useMemo(() => {
    const baseArgs = message?.toolInfo?.arguments;
    try {
      if (typeof baseArgs === 'string') {
        try { JSON.parse(baseArgs); } catch (_) {}
        return baseArgs;
      }
      if (baseArgs && typeof baseArgs === 'object') {
        return JSON.stringify(baseArgs, null, 2);
      }
    } catch (_) {}
    return '{}';
  }, [message]);

  // Try to extract chart data from various shapes including nested execution_steps
  const getChartDataFromSteps = (steps) => {
    const arr = Array.isArray(steps) ? steps : [];
    // As per backend behavior: the refresh endpoint typically puts chart_data in the 2nd step (index 1)
    const second = arr[1]?.result;
    if (second) {
      const cd =
        second?.payload?.chart_data ||
        second?.payload?.chartData ||
        second?.chart_data ||
        second?.chartData ||
        null;
      if (cd) return cd;
    }
    // Fallback: scan from the end
    const step = arr
      .slice()
      .reverse()
      .find(
        s =>
          s?.result?.payload?.chart_data ||
          s?.result?.payload?.chartData ||
          s?.result?.chart_data ||
          s?.result?.chartData
      );
    if (!step?.result) return null;
    const r = step.result;
    return (
      r?.payload?.chart_data ||
      r?.payload?.chartData ||
      r?.chart_data ||
      r?.chartData ||
      null
    );
  };

  const getChartDataFromMessage = () => {
    // BY/Enterprise responses put chart data under payload.chart_data
    // which we store as `message.data`.
    const cd =
      message?.data?.chart_data ||
      message?.data?.chartData ||
      message?.data?.payload?.chart_data ||
      message?.data?.payload?.chartData ||
      message?.payload?.chart_data ||
      message?.payload?.chartData ||
      message?.chart_data ||
      message?.chartData ||
      null;
    if (cd && typeof cd === 'object') return cd;
    // Fallback: sometimes chart_data is produced in a later execution step
    const stepCd =
      getChartDataFromSteps(message?.data?.payload?.execution_steps) ||
      getChartDataFromSteps(message?.payload?.execution_steps) ||
      null;
    return stepCd && typeof stepCd === 'object' ? stepCd : null;
  };

  const isSupportedKendoChart = (chartData) => {
    const inferredType = chartData?.lines?.[0]?.type || chartData?.Lines?.[0]?.Type;
    const t = String(chartData?.type || inferredType || '').toLowerCase();
    return t === 'pie' || t === 'bar' || t === 'line';
  };

  const resolvedType = useMemo(() => {
    try {
      const chartData = getChartDataFromMessage();
      if (chartData && isSupportedKendoChart(chartData)) return 'Chart';
    } catch (_) {}
    return message?.type === 'Chart' ? 'Chart' : 'Grid';
  }, [message]);

  const buildArgumentsString = () => {
    // Return only the arguments data (no wrapper, no extra fields like type/command)
    let parsedArgs = {};
    try {
      parsedArgs = argumentsJson ? JSON.parse(argumentsJson) : {};
    } catch (e) {
      parsedArgs = {};
    }
    return JSON.stringify(parsedArgs);
  };

  const handleSave = async () => {
    if (!name?.trim()) {
      GenerateNotification(
        buildNotification({
          title: 'Name is required',
          description: 'Please provide a tile title.',
          style: 'warning'
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
      return;
    }
    if (!description?.trim()) {
      GenerateNotification(
        buildNotification({
          title: 'Description is required',
          description: 'Please provide a tile description.',
          style: 'warning'
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
      return;
    }

    let analysisCode = '';
    if (message?.toolInfo?.analysis_code) {
      analysisCode = message.toolInfo.analysis_code;
    } else if (message?.toolInfo?.AnalysisCode) {
      analysisCode = message.toolInfo.AnalysisCode;
    } else if (message?.analysis_code) {
      analysisCode = message.analysis_code;
    } else if (message?.AnalysisCode) {
      analysisCode = message.AnalysisCode;
    }

    // Extract analysis id (and optional index for future use)
    let analysisId = '';
    if (message?.toolInfo?.analysis_id) {
      analysisId = message.toolInfo.analysis_id;
    } else if (message?.toolInfo?.AnalysisId) {
      analysisId = message.toolInfo.AnalysisId;
    } else if (message?.analysis_id) {
      analysisId = message.analysis_id;
    } else if (message?.AnalysisId) {
      analysisId = message.AnalysisId;
    }

    const apiKey =
      (typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('SMARTCHAT_SESSION_KEY')
        : null) || '';

    const body = {
      Name: name.trim(),
      Description: description?.trim() || '',
      // Force Type to 'Chart' whenever we detected supported chart_data,
      // otherwise keep original type (defaults to Grid)
      Type: resolvedType,
      FunctionName: functionName || '',
      Arguments: buildArgumentsString(),
      ApiKey: apiKey,
      AnalysisCode: analysisCode,
      // Send the analysis id with the tile as requested
      ...(analysisId ? { AnalysisId: analysisId } : {}),
      Active: true
    };

    try {
      setSaving(true);
      const resp = await addTile(body, assistantAppKey);
      if (resp?.isSuccess) {
        GenerateNotification(
          buildNotification({
            title: 'Tile added',
            description: 'The tile was added to your dashboard.',
            style: 'success'
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );

        // Notify dashboard to refresh tiles immediately (no separate Laravel mapping anymore)
        try {
          const tileId = resp?.payload?.TileId || resp?.Payload?.TileId;
          const dashboardId =
            resp?.payload?.DashboardId || resp?.Payload?.DashboardId || null;
          const tilePayload = resp?.payload || resp?.Payload || null;
          const chartSeed =
            resolvedType === 'Chart' ? getChartDataFromMessage() : null;

          if (tileId) {
            eventBus.dispatch('DASHBOARD_TILE_ADDED', {
              tileId,
              dashboardId,
              appKey: assistantAppKey || null,
              tile: tilePayload,
              chartSeed
            });
          }
        } catch (e) {
          // non-blocking; continue even if event dispatch fails
          console.warn('Failed to dispatch DASHBOARD_TILE_ADDED event:', e);
        }

        onSaved?.(resp?.payload);
      } else {
        GenerateNotification(
          buildNotification({
            title: 'Add Tile failed',
            description: resp?.message || 'Unable to add tile.',
            style: 'error'
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
      }
    } catch (e) {
      GenerateNotification(
        buildNotification({
          title: 'Error',
          description: e.message,
          style: 'error'
        }),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      title="Add to Dashboard"
      onClose={onClose}
      minWidth={520}
      width={640}
    >
      <div className="k-form" style={{ overflowX: 'hidden' }}>
        <div className="k-form-field k-mb-3">
          <label className="k-label k-mb-1 k-required">Title</label>
          <Input
            placeholder="Enter tile title"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>
        <div className="k-form-field k-mb-3">
          <label className="k-label k-mb-1 k-required">Description</label>
          <TextArea
            placeholder="Enter tile description"
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>
      </div>

      <DialogActionsBar>
        <Button
          size="medium"
          fillMode="solid"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          size="medium"
          themeColor="primary"
          fillMode="solid"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default AddToDashboardModal;
